import { asInstanceOf, assert, Listeners, Option, Terminator, UUID } from "@opendaw/lib-std";
import { Address } from "@opendaw/lib-box";
import { Pointers } from "@opendaw/studio-enums";
import { SelectionBox } from "@opendaw/studio-boxes";
import { FilteredSelection } from "./FilteredSelection";
/**
 * Represents the main selection management within a document.
 * This class maintains selections for different users, with each user having their own unique selection.
 */
export class VertexSelection {
    editing;
    boxGraph;
    #lifeTime;
    #entityMap; // sorted on entity
    #selectableMap; // sorted on selectable
    #listeners;
    #target = Option.None;
    constructor(editing, boxGraph) {
        this.editing = editing;
        this.boxGraph = boxGraph;
        this.#lifeTime = new Terminator();
        this.#entityMap = UUID.newSet(entry => entry.box.address.uuid);
        this.#selectableMap = Address.newSet(entry => entry.selectable.address);
        this.#listeners = new Listeners();
    }
    switch(target) {
        this.release();
        console.debug(`VertexSelection.switch(${target.address.toString()})`);
        this.#target = Option.wrap(target);
        this.#lifeTime.own(this.#watch(target));
        return this;
    }
    release() {
        if (this.#target.isEmpty()) {
            return;
        }
        this.#target = Option.None;
        this.#lifeTime.terminate();
        this.#selectableMap.forEach(entry => this.#listeners.proxy.onDeselected(entry.selectable));
        this.#selectableMap.clear();
        this.#entityMap.clear();
    }
    createFilteredSelection(affiliate, map) {
        return new FilteredSelection(this, affiliate, map);
    }
    select(...selectables) {
        if (this.#target.isEmpty()) {
            console.debug(`Cannot select without a user`);
            return;
        }
        if (selectables.length === 0) {
            return;
        }
        this.editing.modify(() => {
            const selection = this.#target.unwrap();
            for (const selectable of selectables) {
                if (!this.#selectableMap.hasKey(selectable.address)) {
                    SelectionBox.create(this.boxGraph, UUID.generate(), box => {
                        box.selectable.refer(selectable);
                        box.selection.refer(selection);
                    });
                }
            }
        }, false);
    }
    deselect(...selectables) {
        if (this.#target.isEmpty()) {
            console.debug(`Cannot deselect without a user`);
            return;
        }
        if (selectables.length === 0) {
            return;
        }
        this.editing.modify(() => selectables
            .forEach(selectable => this.#selectableMap.get(selectable.address).box.delete()), false);
    }
    deselectAll() {
        this.deselect(...this.#selectableMap.values().map(entry => entry.selectable));
    }
    distance(inventory) {
        const excludes = [];
        for (const selectable of inventory) {
            if (!this.#selectableMap.hasKey(selectable.address)) {
                excludes.push(selectable);
            }
        }
        return excludes;
    }
    isEmpty() { return this.#selectableMap.size() === 0; }
    count() { return this.#selectableMap.size(); }
    isSelected(selectable) { return this.#selectableMap.hasKey(selectable.address); }
    selected() { return this.#selectableMap.values().map(entry => entry.selectable); }
    subscribe(listener) { return this.#listeners.subscribe(listener); }
    catchupAndSubscribe(listener) {
        this.selected().forEach(element => listener.onSelected(element));
        return this.#listeners.subscribe(listener);
    }
    #watch(target) {
        return target.pointerHub.catchupAndSubscribe({
            onAdded: (pointer) => {
                const box = asInstanceOf(pointer.box, SelectionBox);
                assert(box.isAttached(), "SelectionBox is not attached");
                const selectable = box.selectable.targetVertex
                    .unwrap(() => `SelectionBox has no target (${box.selectable.targetAddress.unwrapOrUndefined()
                    ?.toString() ?? "No address"})`);
                const entry = { box, selectable };
                this.#listeners.proxy.onSelected(selectable);
                assert(this.#entityMap.add(entry), "Could not add to entityMap");
                assert(this.#selectableMap.add(entry), "Could not add to selectableMap");
            },
            onRemoved: (pointer) => {
                const box = asInstanceOf(pointer.box, SelectionBox);
                const entry = this.#entityMap.removeByKey(box.address.uuid);
                assert(entry.box === box, "Broken selection");
                const selectable = entry.selectable;
                this.#listeners.proxy.onDeselected(selectable);
                this.#selectableMap.removeByKey(selectable.address);
            }
        }, Pointers.Selection);
    }
}
